"use client";

import { useState, useRef, useEffect, useCallback } from "react";
import { useRouter } from "next/navigation";
import { Languages, Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { locales, localeNames, type Locale } from "@/i18n/config";

interface LanguageSelectorProps {
  currentLocale: Locale;
  /** Compact mode for sidebar footer */
  compact?: boolean;
}

export function LanguageSelector({
  currentLocale,
  compact = false,
}: LanguageSelectorProps) {
  const [open, setOpen] = useState(false);
  const [switching, setSwitching] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const router = useRouter();

  // Close on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  const handleSelect = useCallback(
    async (locale: Locale) => {
      if (locale === currentLocale) {
        setOpen(false);
        return;
      }
      setSwitching(true);
      try {
        await fetch("/api/locale", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ locale }),
        });
        // Refresh the page to pick up the new locale
        router.refresh();
        setOpen(false);
      } catch {
        // Silently fail — user can try again
      } finally {
        setSwitching(false);
      }
    },
    [currentLocale, router]
  );

  return (
    <div ref={ref} className="relative">
      <button
        onClick={() => setOpen(!open)}
        disabled={switching}
        className={cn(
          "flex items-center gap-2 rounded-lg border border-border transition-colors",
          "text-foreground/80 hover:bg-accent disabled:opacity-50",
          compact ? "px-2.5 py-1.5 text-xs w-full" : "px-3 py-2 text-sm"
        )}
      >
        <Languages className={compact ? "h-3.5 w-3.5" : "h-4 w-4"} />
        <span className="flex-1 text-left truncate">
          {localeNames[currentLocale]}
        </span>
        <ChevronDown
          className={cn(
            "shrink-0 transition-transform",
            compact ? "h-3 w-3" : "h-3.5 w-3.5",
            open && "rotate-180"
          )}
        />
      </button>

      {open && (
        <div
          className={cn(
            "absolute z-50 mt-1 rounded-lg border border-border bg-card shadow-lg overflow-hidden",
            compact
              ? "bottom-full mb-1 left-0 right-0"
              : "top-full left-0 w-52"
          )}
        >
          <div className="max-h-[280px] overflow-y-auto py-1">
            {locales.map((locale) => (
              <button
                key={locale}
                onClick={() => handleSelect(locale)}
                className={cn(
                  "flex w-full items-center gap-2.5 px-3 py-2 text-left transition-colors",
                  locale === currentLocale
                    ? "bg-brand/10 text-brand"
                    : "text-foreground/80 hover:bg-accent"
                )}
              >
                <span className="text-sm flex-1">{localeNames[locale]}</span>
                <span className="text-[10px] text-muted-foreground uppercase">
                  {locale}
                </span>
                {locale === currentLocale && (
                  <Check className="h-3.5 w-3.5 text-brand shrink-0" />
                )}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
